/*
 * Copyright 2024 Mifos Initiative
 *
 * This Source Code Form is subject to the terms of the Mozilla Public
 * License, v. 2.0. If a copy of the MPL was not distributed with this
 * file, You can obtain one at https://mozilla.org/MPL/2.0/.
 *
 * See https://github.com/openMF/mobile-wallet/blob/master/LICENSE.md
 */
package org.mifospay.feature.mpay.qr.scan

import androidx.lifecycle.ViewModel
import co.touchlab.kermit.Logger
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asSharedFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.serialization.json.Json
import org.mifospay.core.data.util.MpayQrCodeProcessor
import org.mifospay.core.data.util.QrRouteResult
import org.mifospay.core.data.util.QrTransferRouter
import org.mifospay.core.model.beneficiary.Beneficiary
import org.mifospay.core.model.utils.QrCodeData

/**
 * ViewModel for QR code scanning screen.
 *
 * Uses [QrTransferRouter] for smart routing:
 * - Compares FSP IDs to determine intra-bank vs inter-bank
 * - Routes to appropriate transfer flow based on the comparison
 */
class ScanQrViewModel(
    private val qrTransferRouter: QrTransferRouter,
) : ViewModel() {

    private val _eventFlow = MutableStateFlow<ScanQrEvent?>(null)
    val eventFlow = _eventFlow.asSharedFlow()

    private val _isTorchEnabled = MutableStateFlow(false)
    val isTorchEnabled: StateFlow<Boolean> = _isTorchEnabled.asStateFlow()

    private val _showHelpDialog = MutableStateFlow(false)
    val showHelpDialog: StateFlow<Boolean> = _showHelpDialog.asStateFlow()

    private val _isProcessingImage = MutableStateFlow(false)
    val isProcessingImage: StateFlow<Boolean> = _isProcessingImage.asStateFlow()

    private val _selectedImageBytes = MutableStateFlow<ByteArray?>(null)
    val selectedImageBytes: StateFlow<ByteArray?> = _selectedImageBytes.asStateFlow()

    fun toggleTorch() {
        _isTorchEnabled.update { !it }
    }

    fun setTorchEnabled(enabled: Boolean) {
        _isTorchEnabled.value = enabled
    }

    fun showHelpDialog() {
        _showHelpDialog.value = true
    }

    fun hideHelpDialog() {
        _showHelpDialog.value = false
    }

    fun setProcessingImage(processing: Boolean) {
        _isProcessingImage.value = processing
    }

    fun setSelectedImageBytes(bytes: ByteArray?) {
        _selectedImageBytes.value = bytes
    }

    fun clearSelectedImage() {
        _selectedImageBytes.value = null
        _isProcessingImage.value = false
    }

    fun onImageQrScanned(data: String?) {
        _isProcessingImage.value = false
        if (data != null) {
            _eventFlow.update {
                ScanQrEvent.OnScanSuccess
            }
            onScanned(data)
        } else {
            _selectedImageBytes.value = null
            _eventFlow.update {
                ScanQrEvent.OnNoQrFound
            }
        }
    }

    fun onScanned(data: String): Boolean {
        return try {
            Logger.d { "QR Raw Data: $data" }
            val qrCodeData = MpayQrCodeProcessor.decodeMpayString(data)

            Logger.d { "QR Decoded - fspId: ${qrCodeData.fspId}, type: ${qrCodeData.type}, clientId: ${qrCodeData.clientId}, accountId: ${qrCodeData.accountId}" }

            // Use smart routing based on FSP ID comparison
            val routeResult = qrTransferRouter.routeQrScan(qrCodeData)

            _eventFlow.update {
                ScanQrEvent.OnScanSuccess
            }

            // Navigate based on routing result
            when (routeResult) {
                is QrRouteResult.IntraBank -> {
                    Logger.d { "QR Route -> Intra-bank transfer" }
                    _eventFlow.update {
                        ScanQrEvent.OnNavigateToIntraBankTransfer(routeResult.qrData)
                    }
                }

                is QrRouteResult.InterBank -> {
                    Logger.d { "QR Route -> Inter-bank transfer to ${routeResult.accountExternalId}" }
                    _eventFlow.update {
                        ScanQrEvent.OnNavigateToInterbankTransfer(
                            accountExternalId = routeResult.accountExternalId,
                            recipientName = routeResult.recipientName ?: "",
                            amount = routeResult.amount ?: "",
                        )
                    }
                }

                is QrRouteResult.Error -> {
                    Logger.w { "QR Route -> Error: ${routeResult.message}" }
                    _eventFlow.update {
                        ScanQrEvent.ShowToast(routeResult.message)
                    }
                }
            }

            true
        } catch (e: Exception) {
            Logger.e(e) { "QR decode failed, trying fallback" }
            getQrCodeResult(data)
        }
    }

    private fun getQrCodeResult(data: String): Boolean {
        val trimmedData = data.trim()
        Logger.d { "QR scanned (fallback parsing): $data" }

        if (!trimmedData.startsWith("{") || !trimmedData.endsWith("}")) {
            _eventFlow.update {
                ScanQrEvent.ShowToast("Scan a Valid QR Code")
            }
            return false
        }

        return try {
            val beneficiary = parseBeneficiaryFromJson(trimmedData)
            if (beneficiary != null) {
                val beneficiaryString = Json.encodeToString<Beneficiary>(beneficiary)
                _eventFlow.update {
                    ScanQrEvent.OnScanSuccess
                }
                _eventFlow.update {
                    ScanQrEvent.OnNavigateToAddBeneficiary(beneficiaryString)
                }
                true
            } else {
                _eventFlow.update {
                    ScanQrEvent.ShowToast("Scan a Valid QR Code")
                }
                false
            }
        } catch (_: Exception) {
            _eventFlow.update {
                ScanQrEvent.ShowToast("Scan a Valid QR Code")
            }
            false
        }
    }

    private fun parseBeneficiaryFromJson(jsonString: String): Beneficiary? {
        return try {
            Json.decodeFromString<Beneficiary>(jsonString)
        } catch (e: Exception) {
            null
        }
    }
}

sealed interface ScanQrEvent {
    data class OnNavigateToIntraBankTransfer(val qrData: QrCodeData) : ScanQrEvent
    data class OnNavigateToInterbankTransfer(
        val accountExternalId: String,
        val recipientName: String,
        val amount: String,
    ) : ScanQrEvent
    data class OnNavigateToAddBeneficiary(val beneficiary: String) : ScanQrEvent
    data class ShowToast(val message: String) : ScanQrEvent
    data object OnScanSuccess : ScanQrEvent
    data object OnNoQrFound : ScanQrEvent
}
